import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, useTheme } from '@mui/material';
import {
  School as SchoolIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';

function displayName(firstName?: string, lastName?: string, email?: string, nickname?: string): string {
  const full = `${firstName || ''} ${lastName || ''}`.trim();
  if (full) return full.slice(0, 40);
  if (nickname?.trim()) return nickname.trim().slice(0, 40);
  if (email?.trim()) return email.split('@')[0].slice(0, 40);
  return 'User';
}

function uiAvatarsUrl(name: string): string {
  const n = (name || 'User').trim().slice(0, 40) || 'User';
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(n)}&size=256&background=4A90D9&color=fff&bold=true`;
}

const API_BASE = ((import.meta.env.VITE_API_BASE_URL as string) || '').replace(/\/$/, '');

/** True for third-party image hosts (ui-avatars, Google, etc.) — safe as direct img src. */
function isExternalImageUrl(url: string): boolean {
  if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
  return !(url.includes('/api/v1/files/') && url.includes('/serve'));
}

/**
 * Normalize avatar URL for fetch. Prefer same-origin relative paths so the Vite
 * dev proxy forwards /api → API server (avoids cross-origin img failures).
 */
function toFetchableAvatarUrl(avatarUrl?: string): string | null {
  if (!avatarUrl?.trim()) return null;
  const url = avatarUrl.trim();

  if (url.startsWith('/api/v1/files/') && url.includes('/serve')) {
    return url;
  }

  if (API_BASE && url.startsWith(API_BASE)) {
    const relative = url.slice(API_BASE.length);
    if (relative.startsWith('/api/v1/files/')) {
      return relative;
    }
  }

  if (/^[0-9a-fA-F]{24}$/.test(url)) {
    return `/api/v1/files/${url}/serve`;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (url.startsWith('/')) {
    return url;
  }

  return url;
}

function initialsFromUser(firstName?: string, lastName?: string, email?: string, nickname?: string): string {
  const a = firstName?.[0] || nickname?.[0] || email?.[0] || '?';
  const b = lastName?.[0] || '';
  return `${a}${b}`.toUpperCase();
}

type UserListAvatarProps = {
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  nickname?: string;
  size?: number;
  isTeacher?: boolean;
  isAdmin?: boolean;
};

const UserListAvatar: React.FC<UserListAvatarProps> = ({
  avatarUrl,
  firstName,
  lastName,
  email,
  nickname,
  size = 48,
  isTeacher = false,
  isAdmin = false,
}) => {
  const theme = useTheme();
  const [src, setSrc] = useState<string | undefined>(undefined);
  const initials = initialsFromUser(firstName, lastName, email, nickname);
  const fallbackSrc = useMemo(
    () => uiAvatarsUrl(displayName(firstName, lastName, email, nickname)),
    [firstName, lastName, email, nickname]
  );

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    const load = async () => {
      const fetchUrl = toFetchableAvatarUrl(avatarUrl);

      if (!fetchUrl) {
        if (!cancelled) setSrc(fallbackSrc);
        return;
      }

      if (isExternalImageUrl(fetchUrl)) {
        if (!cancelled) setSrc(fetchUrl);
        return;
      }

      const requestUrl =
        fetchUrl.startsWith('/') && typeof window !== 'undefined'
          ? fetchUrl
          : fetchUrl.startsWith('/')
            ? `${API_BASE}${fetchUrl}`
            : fetchUrl;

      try {
        const res = await fetch(requestUrl, { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setSrc(fallbackSrc);
          return;
        }
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(blobUrl);
      } catch {
        if (!cancelled) setSrc(fallbackSrc);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [avatarUrl, fallbackSrc]);

  const ring = 4;
  const outerSize = size + ring;
  const inset = ring / 2;
  const showRoleBadge = isAdmin || isTeacher;
  const badgeSize = Math.max(16, Math.min(36, Math.round(size * 0.32)));
  const iconSize = badgeSize * 0.55;
  const badgeColor = isAdmin
    ? theme.palette.warning.main
    : theme.palette.secondary.main;

  return (
    <Box
      sx={{
        position: 'relative',
        width: outerSize,
        height: outerSize,
        flexShrink: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          bgcolor: theme.palette.primary.main,
          zIndex: 0,
        }}
      />
      <Avatar
        src={src}
        alt={initials}
        imgProps={{ referrerPolicy: 'no-referrer' }}
        sx={{
          position: 'absolute',
          top: inset,
          left: inset,
          width: size,
          height: size,
          zIndex: 1,
          bgcolor: 'transparent',
          fontSize: size * 0.38,
        }}
      >
        {initials}
      </Avatar>
      {showRoleBadge ? (
        <Box
          aria-label={isAdmin ? 'Admin' : 'Teacher'}
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: badgeSize,
            height: badgeSize,
            borderRadius: '50%',
            bgcolor: badgeColor,
            border: `2px solid ${theme.palette.background.paper}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            transform: 'translate(12%, 12%)',
            pointerEvents: 'none',
          }}
        >
          {isAdmin ? (
            <AdminIcon sx={{ fontSize: iconSize, color: theme.palette.common.white }} />
          ) : (
            <SchoolIcon sx={{ fontSize: iconSize, color: theme.palette.common.white }} />
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default UserListAvatar;
